import { useState } from "react";
import useDetectSizeDevice from "./useDetectSizeDevice";
import { updatePositionFilterExplorer } from "@/store/filterExplorerState";
import type { GoogleMapPlace } from "@/store/getGooglePlaceState";

const request: google.maps.places.AutocompleteRequest = {
  input: "",
  includedPrimaryTypes: [],
};

export type SuggestionGooglePlaceAutoComplete = {
  address: string;
  placeId: string;
  toPlace?: google.maps.places.Place;
};

export type UseGooglePlacesAutoCompleteOutput = {
  suggestions: SuggestionGooglePlaceAutoComplete[];
  fetchAutocompleteSuggestions: (search: string) => Promise<void>;
  fetchFieldsDetails: (place: google.maps.places.Place) => Promise<void>;
  googlePlace: GoogleMapPlace;
};

export default () => {
  const [suggestions, setSuggestions] = useState<
    SuggestionGooglePlaceAutoComplete[]
  >([]);
  const { sm } = useDetectSizeDevice();

  const [googlePlace, setGooglePlace] = useState<GoogleMapPlace>({
    location: null,
    boundingBox: null,
    zoomUp: false,
  });

  const fetchAutocompleteSuggestions = async (
    search: string,
  ): Promise<void> => {
    const { AutocompleteSuggestion } = (await google.maps.importLibrary(
      "places",
    )) as google.maps.PlacesLibrary;

    request.input = search;

    const { suggestions } =
      await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    setSuggestions(
      suggestions.map((suggestion) => ({
        address:
          typeof suggestion.placePrediction?.text.text === "string"
            ? suggestion.placePrediction.text.text
            : suggestion.placePrediction?.text?.text || "",
        placeId: suggestion.placePrediction?.placeId || "",
        toPlace: suggestion.placePrediction?.toPlace(),
      })),
    );
  };

  const fetchFieldsDetails = async (place: google.maps.places.Place) => {
    const fieldDetails = await place.fetchFields({
      fields: ["viewport", "location", "types"],
    });

    setGooglePlace({
      location:
        fieldDetails.place.location?.lat() && fieldDetails.place.location?.lng()
          ? {
              latitude: fieldDetails.place.location.lat(),
              longitude: fieldDetails.place.location.lng(),
            }
          : null,
      boundingBox:
        fieldDetails.place.viewport?.getNorthEast() &&
        fieldDetails.place.viewport?.getSouthWest()
          ? {
              topLeft: {
                latitude: fieldDetails.place.viewport.getNorthEast().lat(),
                longitude: fieldDetails.place.viewport.getNorthEast().lng(),
              },
              bottomRight: {
                latitude: fieldDetails.place.viewport.getSouthWest().lat(),
                longitude: fieldDetails.place.viewport.getSouthWest().lng(),
              },
            }
          : null,
      zoomUp: fieldDetails.place.types?.includes("restaurant") ?? false,
    });

    if (!sm && fieldDetails.place.viewport?.getNorthEast()) {
      updatePositionFilterExplorer({
        topLeft: {
          latitude: fieldDetails.place.viewport.getNorthEast().lat(),
          longitude: fieldDetails.place.viewport.getNorthEast().lng(),
        },
        bottomRight: {
          latitude: fieldDetails.place.viewport.getSouthWest().lat(),
          longitude: fieldDetails.place.viewport.getSouthWest().lng(),
        },
      });
    }
  };

  return {
    suggestions,
    fetchAutocompleteSuggestions,
    fetchFieldsDetails,
    googlePlace,
  };
};
